import {
  HvacArea,
  ProjectHvac,
  ProjectHvacAnswer,
  ProjectHvacArea,
  ProjectHvacGroup,
  ProjectHvacGroupArea,
  ProjectHvacSection,
  ProjectHvacVariant,
} from '@belimo-retrofit-portal/logic';
import { pipe } from 'fp-ts/function';
import * as R from 'fp-ts/ReadonlyRecord';
import { HVAC_AREA_MAP } from 'src/modules/hvac/constants/area';
import { HVAC_GROUP_MAP } from 'src/modules/hvac/constants/group';
import { HvacConfig, HvacConfigSection } from 'src/modules/hvac/types/HvacConfig';
import {
  HvacFormAssessmentData,
  HvacFormAreaData,
  HvacFormSectionAnswerData,
  HvacFormSectionData,
  HvacFormVariantData,
  HvacFormGroupData,
  HvacFormGroupAreaData,
} from 'src/modules/hvac/types/HvacFormData';
import { getDefaultFormSection } from 'src/modules/hvac/utils/getDefaultFormSection';

export function mapHvacStoreToForm(
  store: ProjectHvac,
  config: HvacConfig,
): HvacFormAssessmentData {
  return {
    comment: store.comment,
    prices: store.prices,
    areas: pipe(
      store.areas,
      R.map(mapHvacFormArea),
    ),
    groups: pipe(
      HVAC_GROUP_MAP,
      R.mapWithIndex((group) => store.groups
        .filter((it) => it.group === group)
        .map((it) => mapHvacFormGroup(it, config))),
    ),
  };
}

function mapHvacFormArea(
  store: ProjectHvacArea,
): HvacFormAreaData {
  return {
    enabled: store.enabled,
    thermal: store.thermal,
    electrical: store.electrical,
  };
}

function mapHvacFormGroup(
  store: ProjectHvacGroup,
  config: HvacConfig,
): HvacFormGroupData {
  return {
    title: store.title,
    annotations: store.annotations,
    areas: pipe(
      HVAC_AREA_MAP,
      R.mapWithIndex((area) => mapHvacFormGroupArea(
        store.areas.find((it) => it.area === area) ?? createDefaultGroupArea(area),
        config[store.group][area],
      )),
    ),
  };
}
function mapHvacFormGroupArea(
  store: ProjectHvacGroupArea,
  config: ReadonlyArray<HvacConfigSection>,
): HvacFormGroupAreaData {
  return {
    comment: store.comment,
    visible: store.visible,
    sections: config.map((section) => {
      const data = store.sections.find((it) => it.id === section.id);
      return data
        ? mapHvacFormSection(data, section)
        : getDefaultFormSection(section);
    }),
  };
}

function mapHvacFormSection(
  store: ProjectHvacSection,
  config: HvacConfigSection,
): HvacFormSectionData {
  return {
    enabled: config.deactivation ? store.enabled : true,
    config: config,
    answer: mapHvacFormSectionAnswer(store.answer, config),
  };
}

function mapHvacFormSectionAnswer(
  store: ProjectHvacAnswer,
  config: HvacConfigSection,
): HvacFormSectionAnswerData {
  return {
    actual: mapHvacFormVariant(store.actual, config),
    future: mapHvacFormVariant(store.future, config),
  };
}

function mapHvacFormVariant(
  store: ProjectHvacVariant,
  config: HvacConfigSection,
): HvacFormVariantData {
  return {
    option: config.option.find((it) => it.id === store.option) ?? null,
    additionals: config.additionals.map((it) => ({
      config: it,
      value: store.additionals[it.id] ?? null,
    })),
  };
}

function createDefaultGroupArea(area: HvacArea): ProjectHvacGroupArea {
  return {
    area: area,
    visible: true,
    comment: '',
    sections: [],
  };
}
